import { Image } from '@utmb/design-system/image/Image';
import { Font } from '@utmb/design-system/text/font';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { ColorTypesEnum } from '@utmb/types/colors';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { calculateIndexEvolution } from '@utmb/utils/index-calculation';
import { useEffect, useMemo, useRef, type ReactElement } from 'react';
import { CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import styles from './index-chart.module.scss';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { useTranslation } from 'next-i18next';

export interface IndexChartProps {
    results: RaceYearResultForRunnerView[];
    category?: PiCategoryEnum;
}

interface ChartRow {
    x: number;
    timestamp: number;
    lineY?: number | null;
    scatterY?: number;
    raceName?: string;
    eventName?: string;
    date?: string;
    piCategory?: PiCategoryEnum;
    fill?: string;
    distance?: number;
    elevationGain?: number;
    rank?: number;
    totalRanked?: number;
    rankGender?: number;
    totalRankedGender?: number;
    time?: string;
}

/** Convert a date to a fractional year (e.g. 2019-07-02 → 2019.5). */
function toFractionalYear(date: Date): number {
    const year = date.getFullYear();
    const start = new Date(year, 0, 1).getTime();
    const end = new Date(year + 1, 0, 1).getTime();
    return year + (date.getTime() - start) / (end - start);
}

export function IndexChart({ results, category = PiCategoryEnum.GENERAL }: IndexChartProps): ReactElement {
    const { t } = useTranslation();
    const chartRef = useRef<HTMLDivElement>(null);

    const { chartData, yearTicks, domain, yDomain, yTicks, nullRanges, hideEveryOtherLabel, minYear } = useMemo(() => {
        const validResults = results.filter((r) => r.dateIso && r.index > 0 && !r.isDnf);
        const scatterResults = category === PiCategoryEnum.GENERAL ? validResults : validResults.filter((r) => r.piCategory === category);

        // Map race timestamps to race details so we can attach them to matching
        // evolution timepoints (generateTimePoints guarantees race dates are included).
        const raceMap = new Map<number, RaceYearResultForRunnerView>();
        for (const r of scatterResults) {
            raceMap.set(new Date(r.dateIso).getTime(), r);
        }

        const evolution = calculateIndexEvolution(results, category);
        const merged: ChartRow[] = evolution.map((p) => {
            const race = raceMap.get(p.date);
            const row: ChartRow = {
                x: toFractionalYear(new Date(p.date)),
                timestamp: p.date,
                lineY: p.index,
            };
            if (race) {
                row.scatterY = race.index;
                row.raceName = race.raceName;
                row.eventName = race.eventName;
                row.date = race.date;
                row.piCategory = race.piCategory;
                row.fill = getCategoryColor(race.piCategory);
                row.distance = race.distance;
                row.elevationGain = race.elevationGain;
                row.rank = race.rank;
                row.totalRanked = race.totalRanked;
                row.rankGender = race.rankGender;
                row.totalRankedGender = race.totalRankedGender;
                row.time = race.time;
            }
            return row;
        });

        // Safety net: any race whose exact timestamp isn't in the evolution points.
        const matchedTimestamps = new Set(evolution.map((p) => p.date));
        for (const r of scatterResults) {
            const ts = new Date(r.dateIso).getTime();
            if (!matchedTimestamps.has(ts)) {
                merged.push({
                    x: toFractionalYear(new Date(ts)),
                    timestamp: ts,
                    scatterY: r.index,
                    raceName: r.raceName,
                    eventName: r.eventName,
                    date: r.date,
                    piCategory: r.piCategory,
                    fill: getCategoryColor(r.piCategory),
                    distance: r.distance,
                    elevationGain: r.elevationGain,
                    rank: r.rank,
                    totalRanked: r.totalRanked,
                    rankGender: r.rankGender,
                    totalRankedGender: r.totalRankedGender,
                    time: r.time,
                });
            }
        }

        if (merged.length === 0) {
            return {
                chartData: [] as ChartRow[],
                yearTicks: [] as number[],
                domain: [0, 1] as [number, number],
                yDomain: [0, 1] as [number, number],
                yTicks: [] as number[],
                nullRanges: [] as Array<{ x1: number; x2: number }>,
                hideEveryOtherLabel: false,
                minYear: 0,
            };
        }

        const sorted = merged.sort((a, b) => a.x - b.x);

        const allYears = sorted.map((p) => Math.floor(p.x));
        const currentYear = new Date().getFullYear();
        const minYear = Math.min(...allYears);
        const maxYear = Math.max(Math.max(...allYears), currentYear);

        const ticks: number[] = [];
        for (let y = minYear; y <= maxYear; y++) {
            ticks.push(y);
        }
        // When the span exceeds 15 years, still render every tick mark but
        // hide the label on every other year to avoid crowding.
        const hideEveryOtherLabel = maxYear - minYear + 1 > 15;

        // Y-axis: snap to hundreds with a 50-point midpoint rule.
        const indexValues: number[] = [];
        for (const row of sorted) {
            if (row.scatterY !== undefined) indexValues.push(row.scatterY);
            if (row.lineY !== undefined && row.lineY !== null) indexValues.push(row.lineY);
        }
        // Fallback range when no valid index values exist (e.g. category filter with no matches).
        const minIndex = indexValues.length > 0 ? Math.min(...indexValues) : 500;
        const maxIndex = indexValues.length > 0 ? Math.max(...indexValues) : 900;
        const yMin = Math.floor((minIndex - 50) / 100) * 100;
        const yMax = Math.ceil((maxIndex + 50) / 100) * 100;

        const yAxisTicks: number[] = [];
        for (let v = yMin; v <= yMax; v += 100) {
            yAxisTicks.push(v);
        }

        // Detect contiguous null regions in the evolution line.
        //
        // GENERAL: skip leading null runs (chart starts at first valid point), but a
        //   trailing null run extends all the way to the end of the chart.
        // Non-GENERAL (e.g. 100M): show "Index invalide" at the start if the runner had
        //   a valid GENERAL index during that period (reference timespan = GENERAL's
        //   first→last non-null range), and extend trailing null runs to the chart end.
        let generalStartX = -Infinity;
        let generalEndX = Infinity;
        if (category !== PiCategoryEnum.GENERAL) {
            const generalEvolution = calculateIndexEvolution(results, PiCategoryEnum.GENERAL);
            const generalNonNullXs = generalEvolution.filter((p) => p.index !== null).map((p) => toFractionalYear(new Date(p.date)));
            if (generalNonNullXs.length > 0) {
                generalStartX = Math.min(...generalNonNullXs);
                generalEndX = Math.max(...generalNonNullXs);
            }
        }

        const ranges: Array<{ x1: number; x2: number }> = [];
        let nullStartX: number | null = null;
        let hasSeenNonNull = false;
        for (let i = 0; i < sorted.length; i++) {
            const row = sorted[i];
            if (!('lineY' in row)) continue;
            const isNull = row.lineY === null || row.lineY === undefined;

            if (isNull) {
                if (nullStartX === null) {
                    if (category === PiCategoryEnum.GENERAL) {
                        // GENERAL: only open a null range after the first non-null.
                        if (hasSeenNonNull) {
                            nullStartX = sorted[i - 1].x;
                        }
                    } else {
                        // Non-GENERAL: once we've seen a valid non-GENERAL value, open a null
                        // range regardless of the general span (the runner was valid, now isn't).
                        // Before any valid value (leading null), only open within the general span
                        // — otherwise we'd flag "Index invalide" before the runner ever raced.
                        if (hasSeenNonNull) {
                            nullStartX = sorted[i - 1].x;
                        } else if (row.x >= generalStartX && row.x <= generalEndX) {
                            nullStartX = row.x;
                        }
                    }
                }
            } else {
                if (nullStartX !== null) {
                    ranges.push({ x1: nullStartX, x2: row.x });
                    nullStartX = null;
                }
                hasSeenNonNull = true;
            }
        }

        // Trailing null range: extend to the right edge of the chart (maxYear + 1),
        // regardless of category — once invalid without a later valid point, stays invalid.
        if (nullStartX !== null) {
            ranges.push({ x1: nullStartX, x2: maxYear + 1 });
        }

        return {
            chartData: sorted,
            yearTicks: ticks,
            domain: [minYear, maxYear + 1] as [number, number],
            yDomain: [yMin, yMax] as [number, number],
            yTicks: yAxisTicks,
            nullRanges: ranges,
            hideEveryOtherLabel,
            minYear,
        };
    }, [results, category]);

    useEffect(() => {
        const el = chartRef.current;
        if (!el) return;
        const [domainStart, domainEnd] = domain;
        const span = domainEnd - domainStart;
        if (span <= 0) return;
        const todayFraction = (toFractionalYear(new Date()) - domainStart) / span;
        const todayPixel = todayFraction * el.scrollWidth;
        const rightBuffer = 100;
        el.scrollLeft = Math.max(0, todayPixel - el.clientWidth + rightBuffer);
    }, [domain]);

    if (chartData.length === 0) {
        return <div></div>;
    }

    return (
        <div className={styles.wrapChart}>
            <div className={styles.chart} ref={chartRef}>
                <ResponsiveContainer width="100%" minWidth={'800px'} height={400}>
                    <ComposedChart data={chartData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={getColorStyle(ColorTypesEnum.BLUE_LIGHT)} />
                        <XAxis
                            type="number"
                            dataKey="x"
                            domain={domain}
                            ticks={yearTicks}
                            tickFormatter={(value: number) => {
                                const year = Math.floor(value);
                                if (hideEveryOtherLabel && (year - minYear) % 2 !== 0) return '';
                                return String(year);
                            }}
                            allowDecimals={false}
                            interval={0}
                            stroke={getColorStyle(ColorTypesEnum.WHITE)}
                            strokeWidth={2}
                            tick={{ fill: getColorStyle(ColorTypesEnum.WHITE), fontFamily: 'var(--utmb-font-oxanium)', fontWeight: 700, dy: 8 }}
                        />
                        <YAxis
                            type="number"
                            name="Index"
                            domain={yDomain}
                            ticks={yTicks}
                            allowDecimals={false}
                            interval={0}
                            stroke={getColorStyle(ColorTypesEnum.WHITE)}
                            strokeWidth={2}
                            tick={{ fill: getColorStyle(ColorTypesEnum.WHITE), fontFamily: 'var(--utmb-font-oxanium)', fontWeight: 700, dx: -8 }}
                        />
                        <Tooltip
                            cursor={{ stroke: getColorStyle(ColorTypesEnum.WHITE) }}
                            content={({ active, payload }) => {
                                if (!active || !payload || payload.length === 0) return null;
                                const row = payload[0].payload as ChartRow;

                                const indexBlock = (value: number) => (
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                        <Image
                                            objectFit="contain"
                                            layout="fixed"
                                            width={90}
                                            height={22}
                                            maxWidth={300}
                                            image={pIdToCloudinary('/Common/categories/index_bg', 'UTMB Index Race')}
                                            addOptions={[CloudinaryTransformEnum.TRIM]}
                                        />
                                        <Font.OxaniumBold as="span" desktop="24" mobile="20" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                                            {value}
                                        </Font.OxaniumBold>
                                    </div>
                                );

                                // On a race: show race details with the index logo + value.
                                if (row.scatterY !== undefined) {
                                    return (
                                        <div
                                            style={{
                                                background: 'white',
                                                boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.1)',
                                                borderRadius: 'var(--utmb-border-radius)',
                                                padding: 12,
                                                fontSize: 12,
                                                textAlign: 'left',
                                            }}
                                        >
                                            <div className={styles.raceTooltip}>
                                                <div className={styles.wrapCategoryDate}>
                                                    <div className={styles.category}>
                                                        <PiCategoryLogo height={20} piCategory={row.piCategory ?? PiCategoryEnum.GENERAL} isDark={false} />
                                                    </div>
                                                    <Font.FuturaMedium
                                                        className={styles.date}
                                                        desktop="12"
                                                        mobile="12"
                                                        color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    >
                                                        {row.date}
                                                    </Font.FuturaMedium>
                                                </div>
                                                <div className={styles.wrapRaceDistance}>
                                                    <Font.FuturaMedium
                                                        className={styles.race}
                                                        desktop="20"
                                                        mobile="20"
                                                        color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    >
                                                        {row.raceName}
                                                    </Font.FuturaMedium>
                                                    <Font.FuturaLight
                                                        className={styles.distance}
                                                        desktop="20"
                                                        mobile="20"
                                                        color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    >
                                                        {row.distance ? `${row.distance} km` : ''}
                                                    </Font.FuturaLight>
                                                </div>
                                                <div className={styles.wrapEventElevation}>
                                                    <Font.FuturaMedium
                                                        className={styles.event}
                                                        desktop="12"
                                                        mobile="12"
                                                        color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    >
                                                        {row.eventName}
                                                    </Font.FuturaMedium>
                                                    <Font.FuturaMedium
                                                        className={styles.elevation}
                                                        desktop="12"
                                                        mobile="12"
                                                        color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                    >
                                                        {row.elevationGain ? `${row.elevationGain} D+` : ''}
                                                    </Font.FuturaMedium>
                                                </div>
                                                <div
                                                    className={styles.wrapColor}
                                                    style={{
                                                        backgroundColor: getCategoryColor(row.piCategory ?? category) + '1A',
                                                        borderColor: getCategoryColor(category) + '4D',
                                                    }}
                                                >
                                                    <div className={styles.wrapScore}>
                                                        <Font.OxaniumMedium
                                                            color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                            className={styles.score}
                                                            desktop="64"
                                                            mobile="64"
                                                        >
                                                            {row.scatterY}
                                                        </Font.OxaniumMedium>
                                                    </div>
                                                    <div
                                                        className={styles.divider}
                                                        style={{
                                                            backgroundColor: getCategoryColor(row.piCategory ?? category),
                                                        }}
                                                    />
                                                    <div className={styles.wrapRankingTime}>
                                                        <div className={styles.wrapScratch}>
                                                            <Font.FuturaBook
                                                                color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                className={styles.label}
                                                                desktop="12"
                                                                mobile="12"
                                                            >
                                                                {t('singleRunner.resultsTable.rank')}
                                                            </Font.FuturaBook>
                                                            <div className={styles.scratch}>
                                                                <Font.OxaniumMedium color={getColorStyle(ColorTypesEnum.WS_PRIMARY)} desktop="30" mobile="30">
                                                                    {row.rank}
                                                                </Font.OxaniumMedium>
                                                                <div className={styles.divider} />
                                                                <Font.OxaniumMedium
                                                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                    className={styles.secondaryLabel}
                                                                    desktop="30"
                                                                    mobile="30"
                                                                >
                                                                    {`${row.totalRanked}`}
                                                                </Font.OxaniumMedium>
                                                            </div>
                                                        </div>
                                                        <div className={styles.wrapGender}>
                                                            <Font.FuturaBook
                                                                color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                className={styles.label}
                                                                desktop="12"
                                                                mobile="12"
                                                            >
                                                                {t('singleRunner.resultsTable.rankGender')}
                                                            </Font.FuturaBook>
                                                            <div className={styles.gender}>
                                                                <Font.OxaniumMedium color={getColorStyle(ColorTypesEnum.WS_PRIMARY)} desktop="30" mobile="30">
                                                                    {row.rankGender}
                                                                </Font.OxaniumMedium>
                                                                <div className={styles.divider} />
                                                                <Font.OxaniumMedium
                                                                    color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                    className={styles.secondaryLabel}
                                                                    desktop="30"
                                                                    mobile="30"
                                                                >
                                                                    {`${row.totalRankedGender}`}
                                                                </Font.OxaniumMedium>
                                                            </div>
                                                        </div>
                                                        <div className={styles.wrapTime}>
                                                            <Font.FuturaBook
                                                                color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                                                className={styles.label}
                                                                desktop="12"
                                                                mobile="12"
                                                            >
                                                                {t('singleRunner.resultsTable.time')}
                                                            </Font.FuturaBook>
                                                            <div className={styles.time}>
                                                                <Font.OxaniumMedium color={getColorStyle(ColorTypesEnum.WS_PRIMARY)} desktop="14" mobile="14">
                                                                    {row.time}
                                                                </Font.OxaniumMedium>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // On the evolution line (off-race): show the evolution index with the logo.
                                if (row.lineY !== undefined && row.lineY !== null) {
                                    const dateLabel = new Date(row.timestamp).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric',
                                    });
                                    return (
                                        <div
                                            style={{
                                                background: 'white',
                                                boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.1)',
                                                borderRadius: 'var(--utmb-border-radius)',
                                                padding: 12,
                                                fontSize: 12,
                                                textAlign: 'left',
                                            }}
                                        >
                                            <div style={{ marginBottom: 6 }}>
                                                <Font.OxaniumRegular as="span" desktop="14" mobile="14" color="var(--utmb-color-border-darked-gray)">
                                                    {dateLabel}
                                                </Font.OxaniumRegular>
                                            </div>
                                            {indexBlock(row.lineY)}
                                        </div>
                                    );
                                }

                                return null;
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="lineY"
                            stroke={category === PiCategoryEnum.GENERAL ? getColorStyle(ColorTypesEnum.WS_SECONDARY) : getCategoryColor(category)}
                            strokeWidth={2}
                            dot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                        {nullRanges.map((range, i) => (
                            <ReferenceArea
                                key={`null-range-${i}`}
                                x1={range.x1}
                                x2={range.x2}
                                y1={yDomain[0] + 2}
                                fill={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                fillOpacity={0.5}
                                stroke="none"
                                label={{
                                    position: 'center',
                                    content: (props) => {
                                        const vb = props.viewBox as { x?: number; y?: number; width?: number; height?: number } | undefined;
                                        if (!vb || vb.x === undefined || vb.y === undefined || vb.width === undefined || vb.height === undefined) {
                                            return null;
                                        }
                                        const { x, y, width, height } = vb;
                                        const cx = x + width / 2;
                                        const cy = y + height / 2;
                                        const labelWidth = Math.min(100, width);
                                        return (
                                            <foreignObject
                                                x={cx - labelWidth / 2}
                                                y={cy - height / 2}
                                                width={labelWidth}
                                                height={height}
                                                style={{ overflow: 'visible' }}
                                            >
                                                <div
                                                    style={{
                                                        width: labelWidth,
                                                        height,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        textAlign: 'center',
                                                        color: getColorStyle(ColorTypesEnum.WHITE),
                                                        fontSize: 12,
                                                        fontFamily: 'var(--utmb-font-oxanium)',
                                                        fontWeight: 700,
                                                        lineHeight: 1.1,
                                                        wordWrap: 'break-word',
                                                        overflowWrap: 'break-word',
                                                    }}
                                                >
                                                    {t('singleRunner.statistics.indexNoValid')}
                                                </div>
                                            </foreignObject>
                                        );
                                    },
                                }}
                            />
                        ))}
                        <ReferenceLine
                            segment={[
                                { x: toFractionalYear(new Date()), y: yDomain[0] + 2 },
                                { x: toFractionalYear(new Date()), y: yDomain[1] },
                            ]}
                            stroke={getColorStyle(ColorTypesEnum.WHITE)}
                            strokeDasharray="5 5"
                            strokeWidth={4}
                            label={{
                                value: String(t('singleRunner.statistics.global.today') ?? ''),
                                position: 'top',
                                dy: -6,
                                fill: getColorStyle(ColorTypesEnum.WHITE),
                                fontSize: 12,
                                fontFamily: 'var(--utmb-font-oxanium)',
                                fontWeight: 700,
                            }}
                        />
                        <Scatter
                            dataKey="scatterY"
                            shape={(props: { cx?: number; cy?: number; payload?: ChartRow }) => {
                                const { cx, cy, payload } = props;
                                if (cx === undefined || cy === undefined || !payload || payload.scatterY === undefined) return <g />;
                                const color = payload.fill ?? '#8884d8';
                                return <circle cx={cx} cy={cy} r={6} fill={color} stroke={color} />;
                            }}
                            activeShape={(props: { cx?: number; cy?: number; payload?: ChartRow }) => {
                                const { cx, cy, payload } = props;
                                if (cx === undefined || cy === undefined || !payload || payload.scatterY === undefined) return <g />;
                                const color = payload.fill ?? '#8884d8';
                                return (
                                    <g>
                                        <circle cx={cx} cy={cy} r={10} fill={color} fillOpacity={0.45} style={{ filter: 'blur(5px)' }} />
                                        <circle cx={cx} cy={cy} r={6} fill={color} stroke={color} />
                                    </g>
                                );
                            }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
